import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, LayoutGrid, List } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getTheme } from "@/lib/theme";
import { api } from "@/api/client";
import { ProductCard } from "@/components/ProductCard";
import { AuthImage } from "@/components/AuthImage";
import { money } from "@/lib/pricing";

export default function Shop() {
  const { selectedBusinessId } = useAuth();
  const theme = getTheme(selectedBusinessId);
  const [products, setProducts] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [info, setInfo] = useState(null);
  const [cats, setCats] = useState([]);
  const [paused, setPaused] = useState(false);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("bj_shop_view") || "grid");

  useEffect(() => {
    localStorage.setItem("bj_shop_view", view);
  }, [view]);

  useEffect(() => {
    if (!selectedBusinessId) return;
    setLoading(true);
    Promise.all([
      api.listProducts(selectedBusinessId),
      api.listSpecials(selectedBusinessId),
      api.getPaymentInfo(selectedBusinessId).catch(() => null),
      api.listBusinesses().catch(() => []),
    ])
      .then(([p, s, i, bizs]) => {
        setProducts(p);
        setSpecials(s);
        setInfo(i);
        const biz = (bizs || []).find((b) => b.id === selectedBusinessId);
        setCats(biz?.categories || []);
        setPaused(!!biz?.ordering_paused);
      })
      .finally(() => setLoading(false));
  }, [selectedBusinessId]);

  const categories = useMemo(() => {
    const list = (cats || []).filter((c) => c.key !== "all");
    return [{ key: "all", label: "All" }, ...list];
  }, [cats]);

  const saleByProduct = useMemo(() => {
    const m = {};
    for (const s of specials) {
      if (s.product_id && s.is_active && s.sale_price != null) {
        m[s.product_id] = { sale_price: s.sale_price, badge_text: s.badge_text };
      }
    }
    return m;
  }, [specials]);

  const pinned = specials.find((s) => s.pinned && s.is_active) || specials.find((s) => s.is_active);
  const activeSpecialsCount = specials.filter((s) => s.is_active).length;

  // Visible, active specials that bundle multiple products → render as highlighted groups.
  const groups = useMemo(() => {
    const byId = {};
    for (const p of products) byId[p.id] = p;
    return specials
      .filter((s) => s.is_visible !== false && s.is_active && Array.isArray(s.product_ids) && s.product_ids.length > 0)
      .map((s) => ({
        special: s,
        items: s.product_ids.map((id) => byId[id]).filter(Boolean),
      }))
      .filter((g) => g.items.length > 0);
  }, [specials, products]);

  const filtered = category === "all" ? products : products.filter((p) => p.category === category);

  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient relative overflow-hidden">
        <div className="hero-shimmer" aria-hidden="true" />
        <div className="container-app py-16 sm:py-20 relative">
          <div className="flex items-center justify-between gap-8">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.35em] mb-3" style={{ color: "var(--accent)" }}>
                {theme.kind}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-2xl animate-fade-up" style={{ color: "var(--hero-fg)" }}>
                {theme.name}
              </h1>
              <p className="mt-4 text-lg max-w-xl animate-fade-up" style={{ color: "color-mix(in srgb, var(--hero-fg) 78%, transparent)", animationDelay: "80ms" }}>
                {info?.tagline || theme.tagline}
              </p>
              <div className="mt-7 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "140ms" }}>
                <a href="#catalog" className="btn-brand px-6 py-3 rounded-full font-semibold" style={{ background: "var(--accent)", color: "#1a1400" }}>
                  Browse the menu
                </a>
                {activeSpecialsCount > 0 && (
                  <Link
                    to="/deals"
                    data-testid="hero-deals-link"
                    className="px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2"
                    style={{ border: "1px solid color-mix(in srgb, var(--hero-fg) 40%, transparent)", color: "var(--hero-fg)" }}
                  >
                    <Sparkles size={16} /> {activeSpecialsCount} live deal{activeSpecialsCount > 1 ? "s" : ""}
                  </Link>
                )}
              </div>
            </div>
            <div className="hidden lg:block shrink-0 crest-wrap">
              <div
                className="crest-glow"
                style={{ background: "radial-gradient(circle, var(--accent) 0%, transparent 70%)", opacity: 0.4 }}
                aria-hidden="true"
              />
              <img
                src={theme.logo}
                alt={theme.name}
                className="crest-float relative h-44 w-44 xl:h-52 xl:w-52 object-contain"
                style={{ filter: "drop-shadow(0 16px 32px rgba(0,0,0,0.5))" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pinned special banner */}
      {pinned && (
        <div className="container-app -mt-8 relative z-10">
          <Link
            to="/deals"
            data-testid="pinned-special-banner"
            className="block rounded-2xl overflow-hidden card-lift"
            style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
          >
            <div className="grid sm:grid-cols-[200px_1fr] items-stretch">
              <div className="h-36 sm:h-full" style={{ background: "var(--surface-2)" }}>
                {pinned.image_url ? (
                  <AuthImage src={pinned.image_url} alt={pinned.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <Sparkles size={40} style={{ color: "var(--accent)" }} />
                  </div>
                )}
              </div>
              <div className="p-5 flex items-center justify-between gap-4">
                <div>
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase" style={{ background: "var(--accent)", color: "#1a1400" }}>
                    {pinned.badge_text || "Special"}
                  </span>
                  <h3 className="font-display text-2xl mt-2" style={{ color: "var(--ink)" }}>
                    {pinned.title}
                  </h3>
                  <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--muted)" }}>
                    {pinned.description}
                  </p>
                  {pinned.sale_price != null && (
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="font-display text-xl" style={{ color: "var(--brand)" }}>{money(pinned.sale_price)}</span>
                      {pinned.original_price != null && (
                        <span className="text-sm line-through" style={{ color: "var(--muted)" }}>{money(pinned.original_price)}</span>
                      )}
                    </div>
                  )}
                </div>
                <ArrowRight size={22} style={{ color: "var(--brand)" }} className="shrink-0" />
              </div>
            </div>
          </Link>
        </div>
      )}

      {/* Featured groups (multi-item specials) */}
      {groups.length > 0 && (
        <div className="container-app pt-12 space-y-10" data-testid="featured-groups">
          {groups.map(({ special, items }) => (
            <section key={special.id} data-testid={`group-section-${special.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={18} style={{ color: "var(--accent)" }} />
                <span className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "var(--accent)" }}>
                  {special.badge_text || "Featured"}
                </span>
              </div>
              <h2 className="font-display text-3xl" style={{ color: "var(--ink)" }}>{special.title}</h2>
              {special.description && (
                <p className="mt-1 max-w-2xl" style={{ color: "var(--muted)" }}>{special.description}</p>
              )}
              <div className="mt-5 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((p, i) => (
                  <ProductCard key={p.id} product={p} sale={saleByProduct[p.id]} index={i} variant="grid" />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Catalog */}
      <section id="catalog" className="container-app pt-14">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h2 className="font-display text-3xl" style={{ color: "var(--ink)" }}>
            The menu
          </h2>
          <div
            className="flex items-center gap-1 rounded-full p-1"
            style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
            data-testid="view-toggle"
          >
            <button
              onClick={() => setView("grid")}
              data-testid="view-grid-btn"
              aria-label="Grid view"
              className="p-2 rounded-full transition"
              style={view === "grid" ? { background: "var(--brand)", color: "var(--brand-fg)" } : { color: "var(--muted)" }}
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="view-list-btn"
              aria-label="List view"
              className="p-2 rounded-full transition"
              style={view === "list" ? { background: "var(--brand)", color: "var(--brand-fg)" } : { color: "var(--muted)" }}
            >
              <List size={16} />
            </button>
          </div>
        </div>

        {!loading && products.length > 0 && (
          <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>
            {products.length} item{products.length === 1 ? "" : "s"} across {categories.length - 1} categories
          </p>
        )}

        {paused && (
          <div className="mt-4 rounded-2xl px-5 py-4 flex items-center gap-3" style={{ background: "#fdecec", border: "1px solid #f3c6c6" }} data-testid="shop-paused-banner">
            <span className="text-2xl">⏸️</span>
            <div>
              <div className="font-display text-lg" style={{ color: "#b91c1c" }}>Not taking orders right now</div>
              <p className="text-sm" style={{ color: "#9b2c2c" }}>You can browse the menu, but checkout is paused. Please check back soon.</p>
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              data-testid={`category-${c.key}`}
              className="shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition"
              style={
                category === c.key
                  ? { background: "var(--brand)", color: "var(--brand-fg)" }
                  : { background: "var(--surface)", color: "var(--muted)", border: "1px solid var(--border)" }
              }
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                <div className="aspect-[4/3] animate-pulse" style={{ background: "var(--surface-2)" }} />
                <div className="p-4 space-y-2">
                  <div className="h-3 w-1/3 rounded animate-pulse" style={{ background: "var(--surface-2)" }} />
                  <div className="h-4 w-2/3 rounded animate-pulse" style={{ background: "var(--surface-2)" }} />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-10 text-center py-16 rounded-2xl" style={{ background: "var(--surface)", border: "1px dashed var(--border)" }}>
            <p className="font-display text-xl" style={{ color: "var(--ink)" }}>Nothing here yet</p>
            <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>Try another category.</p>
          </div>
        ) : (
          <div
            className={view === "grid" ? "mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" : "mt-8 flex flex-col gap-4"}
            data-testid="product-list"
          >
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} sale={saleByProduct[p.id]} index={i} variant={view} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
